/**
 * Shared currency formatting utilities.
 *
 * Replaces 3+ different implementations scattered across components.
 */

const fullFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2
});

const compactFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 0,
	maximumFractionDigits: 0
});

const signedFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
	signDisplay: 'always'
});

/** Format as full currency: $1,234.56 */
export function formatCurrency(amount: number): string {
	return fullFormatter.format(amount);
}

/**
 * Round a raw amount-input string to 2 decimal places, e.g. "5" -> "5.00".
 * Empty or non-numeric input passes through unchanged (lets required/min
 * validation on the input handle it).
 */
export function formatAmountInput(value: string): string {
	if (value.trim() === '') return value;
	const n = Number(value);
	if (!Number.isFinite(n)) return value;
	return n.toFixed(2);
}

/** Format as compact currency (no decimals): $1,235 */
export function formatCompactCurrency(amount: number): string {
	return compactFormatter.format(amount);
}

/** Format with explicit sign: +$1,234.56 / -$1,234.56 */
export function formatSignedCurrency(amount: number): string {
	return signedFormatter.format(amount);
}

/**
 * Format a credit account balance contextually:
 * - negative → "$X owed"
 * - zero → "Paid off"
 * - positive → "$X credit"
 */
export function formatCreditBalance(balance: number): string {
	if (balance < 0) return `${fullFormatter.format(Math.abs(balance))} owed`;
	if (balance === 0) return 'Paid off';
	return `${fullFormatter.format(balance)} credit`;
}

/**
 * Format a credit account balance in compact form:
 * - negative → "$X owed"
 * - zero → "Paid off"
 * - positive → "$X credit"
 */
export function formatCompactCreditBalance(balance: number): string {
	if (balance < 0) return `${compactFormatter.format(Math.abs(balance))} owed`;
	if (balance === 0) return 'Paid off';
	return `${compactFormatter.format(balance)} credit`;
}

/**
 * Format a balance contextually: for credit accounts shows "owed"/"Paid off"/"credit",
 * for non-credit accounts shows compact currency.
 */
export function formatBalance(value: number, isCredit: boolean): string {
	if (!isCredit) return compactFormatter.format(value);
	return formatCompactCreditBalance(value);
}

/**
 * Get the contextual label for a transfer involving a credit account.
 * Returns null if neither account is credit (use default display).
 */
export function getTransferLabel(
	sourceAccountType: string | null | undefined,
	transferAccountType: string | null | undefined,
	sourceAccountName: string | null | undefined,
	transferAccountName: string | null | undefined
): string | null {
	if (transferAccountType === 'credit') {
		return `Credit Card Payment${transferAccountName ? ` → ${transferAccountName}` : ''}`;
	}
	if (sourceAccountType === 'credit') {
		return `Cash Advance${sourceAccountName ? ` from ${sourceAccountName}` : ''}`;
	}
	return null;
}

/**
 * Perspective-aware transfer label. Current account always renders first.
 * amount < 0 → current is source → "{source} → {dest}"
 * amount >= 0 → current is destination → "{dest} ← {source}"
 */
export function formatTransferLabel(params: {
	amount: number;
	sourceAccountName: string | null | undefined;
	transferAccountName: string | null | undefined;
	sourceAccountType?: string | null | undefined;
	transferAccountType?: string | null | undefined;
}): string {
	const { amount, sourceAccountName, transferAccountName, sourceAccountType, transferAccountType } =
		params;
	const isSource = amount < 0;

	if (transferAccountType === 'credit') {
		return isSource
			? `Credit Card Payment${transferAccountName ? ` → ${transferAccountName}` : ''}`
			: `Credit Card Payment${sourceAccountName ? ` ← ${sourceAccountName}` : ''}`;
	}

	if (sourceAccountType === 'credit') {
		return isSource
			? `Cash Advance${transferAccountName ? ` → ${transferAccountName}` : ''}`
			: `Cash Advance${sourceAccountName ? ` from ${sourceAccountName}` : ''}`;
	}

	if (isSource) {
		if (sourceAccountName && transferAccountName)
			return `${sourceAccountName} → ${transferAccountName}`;
		if (transferAccountName) return `→ ${transferAccountName}`;
		if (sourceAccountName) return `${sourceAccountName} →`;
		return 'Transfer';
	} else {
		if (sourceAccountName && transferAccountName)
			return `${transferAccountName} ← ${sourceAccountName}`;
		if (sourceAccountName) return `← ${sourceAccountName}`;
		if (transferAccountName) return `${transferAccountName} ←`;
		return 'Transfer';
	}
}
